import { Modal } from "@mui/material";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DeliveryProduct, DeliveryTrip, ProductOrder } from "../../../../../data/entities";
import { DELIVERY_TRIP_STATUS } from "../../../../../data/enums";
import CancelModal from "../../../../../shared/components/CancelModal";
import CustomTable from "../../../../../shared/components/CustomTable";
import Form, { FieldConfigs } from "../../../../../shared/components/FormType";
import { StyledButton } from "../../../../../shared/styled/TitleBar";
import apiMethods from "../../../../../utils/api";
import { error, success } from "../../../../../utils/toast";
import { aggregateDeliveryRows, DeliveryRow, entityKey, expandDeliveryRows, rowKey, singleDeliveryAddress } from "../deliveryAllocations";
import { AddDeliveryTripModalRow, AddDeliveryTripTableColumns } from "../index";
import { ButtonRow, Expandable, ModalWrapper, StyledBackdrop, TitleText, TitleWrapper } from "./Styled";
import LoadingButton from "../../../../../shared/components/LoadingButton";
import {
    getDefaultDeliveryAddress,
} from "../deliveryAddress";
import { getDeliveryQuantityWarning, getRemainingDeliveryQuantity, getSuggestedDeliveryQuantity } from "../deliveryQuantity";

interface AddDeliveryTripModalProps {
    title: string;
    selectedDeliveryTrip: DeliveryTrip;
    open: boolean;
    handleClose: any;
    mergeDeliveryTrip: Function;
    fetchProductOrder?: () => Promise<void>;
    productOrder: ProductOrder;
}

export const AddDeliveryTripModal = React.memo(({
                                                    title,
                                                    selectedDeliveryTrip,
                                                    open,
                                                    productOrder,
                                                    handleClose,
                                                    mergeDeliveryTrip,
                                                    fetchProductOrder,
                                                }: AddDeliveryTripModalProps) => {
    // Simple cache for selected car plates: UUID -> plate number
    const [selectedCarPlateCache, setSelectedCarPlateCache] = useState<Map<string, string>>(new Map());
    const canEdit = isEmpty(selectedDeliveryTrip) || String(selectedDeliveryTrip?.status || "").toUpperCase() === DELIVERY_TRIP_STATUS.PENDING;
    const isEditingTrip = !isEmpty(selectedDeliveryTrip);
    const draftKey = `${productOrder.id}:${isEditingTrip ? selectedDeliveryTrip.id || "existing" : "new"}`;
    const initializedDraftRef = useRef<string | null>(null);
    const defaultAddress = productOrder.delivery_address || getDefaultDeliveryAddress(productOrder.customer?.delivery_addresses || []);
    const previousDefaultAddressRef = useRef(defaultAddress);

    const tripNumberLocked = Boolean(selectedDeliveryTrip?.is_exported || selectedDeliveryTrip?.is_first_storage_keeper_exported || selectedDeliveryTrip?.is_second_storage_keeper_exported);
    const [baseFormFields] = useState<FieldConfigs[]>([
        {
            label: "Biển số xe",
            key: "car_plate",
            type: "textanddropdown",
            options: [],
            fetchItems: async (search: string): Promise<{ text: string; value: string }[]> => {
                try {
                    const response: any = await apiMethods.get(`/product-flow/v2/delivery-trips/car-plates/?search=${search}`);
                    return response.map((carPlate: { plate_number: string; id: string }) => ({
                        text: carPlate.plate_number,
                        value: carPlate.plate_number,
                    }));
                } catch (error) {
                    console.error("Error fetching car plates:", error);
                    return [];
                }
            },
            onChange: (selectedItem: any) => {
                if (selectedItem && selectedItem.value) {
                    handleCarPlateSelection(selectedItem);
                }
            },
            required: false,
            customProps: {
                dropdownContainerStyle: { maxHeight: "100px" },
            },
            disabled: !canEdit,
        },
        {
            label: "Ngày giao hàng",
            key: "delivery_date",
            type: "datetime",
            required: false,
            customProps: { clearable: true, format: "DD/MM/YYYY HH:mm" },
            disabled: !canEdit,
        },
        {
            label: "Chuyến", key: "trip_number", type: "dropdown",
            options: [{ text: "Chưa chọn", value: "" }, ...[1, 2, 3, 4, 5].map(n => ({ text: `Chuyến ${n}`, value: String(n) }))],
            required: false, disabled: !canEdit || tripNumberLocked,
        },
        {
            label: "Ghi chú",
            key: "delivery_note",
            type: "text",
            required: false,
            disabled: !canEdit,
        },
    ]);
    const formFields = useMemo(() => baseFormFields.map((field) => ({
        ...field,
        disabled: !canEdit || (field.key === "trip_number" && tripNumberLocked),
    })), [baseFormFields, canEdit, tripNumberLocked]);

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelModalMessage, setCancelModalMessage] = useState("");
    const [preparedDeliveryTrip, setPreparedDeliveryTrip] = useState<DeliveryTrip | null>(null);
    const [preparedDeliveryTrips, setPreparedDeliveryTrips] = useState<DeliveryTrip[] | null>(null);
    const [isOpen, setIsOpen] = useState(open);
    const [isSaving, setIsSaving] = useState(false);
    const isSavingRef = useRef(false);

    const [selectedItems, setSelectedItems] = useState<DeliveryRow[]>([]);

    const [removedItems, setRemovedItems] = useState<DeliveryRow[]>([]);

    const [formData, setFormData] = useState({
        trip_number: "",
        car_plate: "",
        delivery_date: new Date() as Date | number | null,
        delivery_note: "",
    });

    const [validated, setValidated] = useState(false);

    // Update local state when form data changes
    const handleFormDataChange = (updatedFormData: any) => {
        if (updatedFormData.delivery_date) {
            updatedFormData.delivery_date = updatedFormData.delivery_date;
        }
        setFormData(updatedFormData);
    };

    // Custom handler for car plate selection that stores the plate number
    const handleCarPlateSelection = (selectedItem: { text: string; value: string }) => {
        // Store the plate number in cache
        setSelectedCarPlateCache(prev => new Map(prev).set(selectedItem.value, selectedItem.text));

        // Update form data
        setFormData(prev => ({
            ...prev,
            car_plate: selectedItem.value,
        }));
    };

    const removeSelectedItem = (item: DeliveryRow) => {
        if (!canEdit || item.returned_quantity) return;
        setSelectedItems(rows => rows.filter(row => row.rowKey !== item.rowKey));
        setRemovedItems(rows => [...rows, item]);
    };
    const splitItem = (item: DeliveryRow) => {
        if (!canEdit) return;
        setSelectedItems(rows => rows.flatMap(row => row.rowKey === item.rowKey
            ? [row, {...row, rowKey: rowKey(), quantity: 0, returned_quantity: 0}] : [row]));
    };

    const handleCloseWithAnimation = () => {
        if (isSaving) return;
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    const validSelectedItems = selectedItems.filter(
        (item) => item && item.id,
    );

    const deliveryTrip: DeliveryTrip = useMemo(() => {
        return {
            ...(isEmpty(selectedDeliveryTrip)
                ? {
                    product_order_delivery: productOrder.id,
                    // Use new backend field names
                    vehicle_plate: formData.car_plate,
                    trip_number: formData.trip_number ? Number(formData.trip_number) : null,
                    scheduled_at: formData.delivery_date == null ? null : dayjs(formData.delivery_date).utc().format("YYYY-MM-DDTHH:mm:ss"),
                    delivery_address: singleDeliveryAddress(selectedItems),
                    notes: formData.delivery_note || "No notes",
                    carrier_name: "Unknown Carrier",
                    carrier_phone: "N/A",
                    returned_quantity: 0,
                    status: "PENDING",
                    delivery_products: [],
                    // Keep old fields for backward compatibility
                    car_plate: formData.car_plate,
                    date: formData.delivery_date == null ? undefined : new Date(formData.delivery_date),
                    note: formData.delivery_note,
                }
                : selectedDeliveryTrip),
            delivery_products: validSelectedItems,
        };
    }, [selectedDeliveryTrip, validSelectedItems, formData]);


    const calculateDeliveredQuantities = (deliveryTrips: DeliveryTrip[]) => {
        const deliveredQuantities: Record<string, number> = {};

        deliveryTrips.forEach((trip) => {
            (trip.delivery_products || []).forEach((item) => {
                const itemId = item.product?.id || item.material?.id;
                const quantity = item.quantity || 0;

                if (itemId) {
                    if (!deliveredQuantities[itemId]) {
                        deliveredQuantities[itemId] = 0;
                    }

                    deliveredQuantities[itemId] += quantity;
                }
            });
        });

        return deliveredQuantities;
    };

    const validateQuantities = (productOrder: ProductOrder, deliveryTrips: DeliveryTrip[]): string[] => {
        const deliveredQuantities = calculateDeliveredQuantities(deliveryTrips);
        const messages: string[] = [];
        let hasProductWarning = false;
        const originalTripItems: DeliveryProduct[] = (selectedDeliveryTrip as any)?.normalized_items
            || selectedDeliveryTrip?.delivery_products || (selectedDeliveryTrip as any)?.items || [];

        // Validate products
        productOrder.products.forEach((product) => {
            const productId = product.id;
            const selected = aggregateDeliveryRows(selectedItems).find((item) => item.product?.id === productId);
            if (!selected) return;
            const previousQuantity = selectedDeliveryTrip?.id
                ? originalTripItems.filter((item) => item.product?.id === productId).reduce(
                    (total, item) => total + (item.quantity || 0),
                    0,
                ) : undefined;
            const message = getDeliveryQuantityWarning({
                productCode: product.product_code,
                productName: product.name,
                quantity: selected.quantity || 0,
                remaining: getRemainingDeliveryQuantity(product, previousQuantity),
            });
            if (message) {
                messages.push(message);
                hasProductWarning = true;
            }
        });

        // Validate materials
        productOrder.materials?.forEach((material) => {
            const materialId = material.id;
            const requiredQuantity = material.selectedQuantity || 0;
            const deliveredQuantity = deliveredQuantities[materialId] || 0;

            if (deliveredQuantity > requiredQuantity) {
                messages.push(
                    `Vật tư "${material.name}" đã chọn vượt quá số lượng yêu cầu. ` +
                    `Yêu cầu: ${requiredQuantity}, Đã chọn: ${deliveredQuantity}.\n`,
                );
            } else if (deliveredQuantity < requiredQuantity) {
                messages.push(
                    `Vật tư "${material.name}" chưa chọn đủ số lượng yêu cầu. ` +
                    `Yêu cầu: ${requiredQuantity}, Đã chọn: ${deliveredQuantity}.\n`,
                );
            }
        });

        if (hasProductWarning) {
            messages.push("Bạn có muốn tiếp tục lưu chuyến với số lượng này không?");
        }
        return messages;
    };


    const prepareDeliveryTrip = async (thisSelectedItems: DeliveryProduct[]): Promise<DeliveryTrip> => {
        const formattedDeliveryProducts = thisSelectedItems.map(item => ({
            ...item,
            id: item.id,
            quantity: item.quantity,
            product: item.product || undefined,
            material: item.material || undefined,
            returned_quantity: item.returned_quantity || 0,
            item_kind: item.item_kind,
        }));

        // Use cached plate number if available, otherwise use UUID
        const carPlateToSend = selectedCarPlateCache.get(formData.car_plate) || formData.car_plate;

        const prepared: DeliveryTrip = {
            ...deliveryTrip,
            vehicle_plate: carPlateToSend, // Send plate number if cached, otherwise UUID
            trip_number: formData.trip_number ? Number(formData.trip_number) : null,
            scheduled_at: formData.delivery_date == null ? null : dayjs(formData.delivery_date).utc().format("YYYY-MM-DDTHH:mm:ss"),
            delivery_address: singleDeliveryAddress(selectedItems),
            notes: formData.delivery_note || "No notes",
            carrier_name: "Unknown Carrier", // Set default values for required fields
            carrier_phone: "N/A",
            status: "PENDING" as const, // Set valid status
            // Preserve selected items locally so the parent view shows them until API sync
            delivery_products: formattedDeliveryProducts,
        };
        return prepared;
    };

    // Helper: merge saved trip into parent state
    const mergeTripIntoState = (
        tripId: string,
        items: DeliveryProduct[],
        isNewTrip: boolean,
    ) => {
        const baseTrip = {
            id: tripId,
            vehicle_plate: selectedCarPlateCache.get(formData.car_plate) || formData.car_plate,
            car_plate: selectedCarPlateCache.get(formData.car_plate) || formData.car_plate,
            date: formData.delivery_date == null ? undefined : new Date(formData.delivery_date),
            trip_number: formData.trip_number ? Number(formData.trip_number) : null,
            scheduled_at: formData.delivery_date == null ? null : dayjs(formData.delivery_date).utc().toISOString(),
            delivery_address: singleDeliveryAddress(selectedItems),
            notes: formData.delivery_note || "No notes",
            status: isNewTrip ? "PENDING" : (selectedDeliveryTrip?.status || "PENDING"),
        } as Partial<DeliveryTrip>;

        const mappedItems = (items || []).map((it) => ({
            ...it,
            id: it.product?.id || it.material?.id || it.id,
            quantity: it.quantity,
            returned_quantity: it.returned_quantity || 0,
            product: it.product,
            material: it.material,
        }));

        const currentTrips = Array.isArray(productOrder?.delivery_trips) ? (productOrder.delivery_trips as DeliveryTrip[]) : [];
        const idx = currentTrips.findIndex((t) => t.id === tripId);
        let mergedTrips: DeliveryTrip[];
        if (idx >= 0) {
            const updatedTrip: DeliveryTrip = {
                ...currentTrips[idx],
                ...baseTrip,
                delivery_products: mappedItems,
                normalized_items: mappedItems,
            } as DeliveryTrip;
            mergedTrips = currentTrips.map((t, i) => (i === idx ? updatedTrip : t));
        } else {
            const newTrip: DeliveryTrip = {
                id: tripId,
                vehicle_plate: String(baseTrip.vehicle_plate || ""),
                trip_number: baseTrip.trip_number,
                scheduled_at: baseTrip.scheduled_at ?? null,
                delivery_address: String(baseTrip.delivery_address || ""),
                notes: String(baseTrip.notes || ""),
                status: (baseTrip.status as any) || "PENDING",
                delivery_products: mappedItems,
            } as DeliveryTrip;
            mergedTrips = [...currentTrips, newTrip];
        }

        mergeDeliveryTrip({ delivery_trips: mergedTrips });
    };


    const buildLineItemMaps = (order: ProductOrder) => {
        const productIdToLineItemId: Record<string, string> = Object.fromEntries(
            (order.products || [])
                .map((p: any) => [p.id, p.line_item_id])
                .filter((x: any[]) => Boolean(x[1])),
        );
        const materialIdToLineItemId: Record<string, string> = Object.fromEntries(
            (order.materials || [])
                .map((m: any) => [m.id, m.line_item_id])
                .filter((x: any[]) => Boolean(x[1])),
        );
        return { productIdToLineItemId, materialIdToLineItemId };
    };

    const fetchTripItems = async (tripId: string) => {
        const resp: any = await apiMethods.get(`/product-flow/v2/delivery-trips/${tripId}/items/`);
        return Array.isArray(resp) ? resp : (resp?.results || []);
    };

    type ReturnItemPayload = { delivery_trip_item_id: string; returned_quantity: number };

    const buildReturnItems = (
        items: any[],
        rawItems: any[],
        productIdToLineItemId: Record<string, string>,
        materialIdToLineItemId: Record<string, string>,
    ): ReturnItemPayload[] => {
        return items
            .filter((it: any) => (it.returned_quantity || 0) >= 0)
            .map((it: any) => {
                if (it.delivery_trip_item_id) {
                    return {
                        delivery_trip_item_id: it.delivery_trip_item_id,
                        returned_quantity: it.returned_quantity || 0,
                    } as ReturnItemPayload;
                }
                const sale_order_item_id = it.product
                    ? productIdToLineItemId[it.product.id]
                    : materialIdToLineItemId[it.material?.id];
                const createdItem = rawItems.find((created: any) => created.sale_order_item_id === sale_order_item_id);
                return createdItem
                    ? {
                        delivery_trip_item_id: createdItem.id,
                        returned_quantity: it.returned_quantity || 0,
                    }
                    : null;
            })
            .filter(Boolean) as ReturnItemPayload[];
    };

    const postReturnQuantities = async (returnItems: ReturnItemPayload[]) => {
        if (returnItems.length === 0) return;
        await Promise.all(
            returnItems.map(async (returnItem) => {
                try {
                    await apiMethods.post(
                        `/product-flow/v2/delivery-trips/items/${returnItem.delivery_trip_item_id}/return/`,
                        {
                            returned_quantity: returnItem.returned_quantity,
                            return_received_at: dayjs().toISOString(),
                        },
                    );
                } catch (err) {
                    console.error(
                        `Failed to update return quantity for item ${returnItem.delivery_trip_item_id}:`,
                        err,
                    );
                    error("Lỗi cập nhật số lượng trả về", err);
                }
            }),
        );
    };

    const handleReturns = async (
        tripId: string,
        items: any[],
        productIdToLineItemId: Record<string, string>,
        materialIdToLineItemId: Record<string, string>,
    ) => {
        if (!items || items.length === 0) return;
        try {
            const rawItems = await fetchTripItems(tripId);
            const returnItems = buildReturnItems(
                items,
                rawItems,
                productIdToLineItemId,
                materialIdToLineItemId,
            );
            await postReturnQuantities(returnItems);
        } catch (e) {
            console.error("Error handling return quantities:", e);
        }
    };

    // Save trip directly to backend (create new or update existing)
    const saveDeliveryTrip = async () => {
        if (isSavingRef.current) return;
        isSavingRef.current = true;
        setIsSaving(true);
        try {
            const savedItems = aggregateDeliveryRows(selectedItems);
            const isNewTrip = !selectedDeliveryTrip?.id;
            let tripId: string;

            if (isNewTrip) {
                // 1) Create a new trip via API
                const tripData = {
                    vehicle_plate: selectedCarPlateCache.get(formData.car_plate) || formData.car_plate,
                    carrier_name: "Unknown Carrier", // Default value
                    carrier_phone: "N/A", // Default value
                    trip_number: formData.trip_number ? Number(formData.trip_number) : null,
                    scheduled_at: formData.delivery_date == null ? null : dayjs(formData.delivery_date).utc().format("YYYY-MM-DDTHH:mm:ss"),
                    notes: formData.delivery_note || "No notes",
                    status: "PENDING",
                    sale_order_id: productOrder.id,
                };

                const createResp: any = await apiMethods.post(
                    `/product-flow/v2/delivery-trips/`,
                    tripData,
                );
                tripId = createResp.id;
            } else {
                // Existing trips save their header and quantities together below.
                tripId = selectedDeliveryTrip.id || "";
            }

            // 2) Map order items for sale_order_item_id resolution (shared helper)
            const { productIdToLineItemId, materialIdToLineItemId } = buildLineItemMaps(productOrder);

            if (isNewTrip) {
                // 3a) Create items for the new trip
                const createItems = savedItems
                    .map((it: any) => {
                        const sale_order_item_id = it.product
                            ? productIdToLineItemId[it.product.id]
                            : materialIdToLineItemId[it.material?.id];
                        return sale_order_item_id
                            ? {
                                sale_order_item_id,
                                item_kind: it.product ? "PRODUCT" : "MATERIAL",
                                quantity: it.quantity,
                                delivery_allocations: it.delivery_allocations,
                            }
                            : null;
                    })
                    .filter(Boolean) as any[];

                if (createItems.length > 0) {
                    await apiMethods.post(
                        `/product-flow/v2/delivery-trips/${tripId}/items/`,
                        { items: createItems },
                    );
                }

                // 3b) Handle return quantities for items (shared helper)
                await handleReturns(tripId, savedItems, productIdToLineItemId, materialIdToLineItemId);
            } else {
                // 3b) Update items for existing trip using the same logic as main component
                if (canEdit) {
                    const updateItems = savedItems
                        .filter((it: any) => !!(it.delivery_trip_item_id || it.id))
                        .map((it: any) => {
                            // New API contract:
                            // - If item has delivery_trip_item_id, send that (existing item)
                            // - If item has product/material info, send product/material ID with item_kind (new item)

                            if (it.delivery_trip_item_id) {
                                // Existing item: send delivery trip item ID without item_kind
                                return {
                                    id: it.delivery_trip_item_id,
                                    quantity: it.quantity,
                                delivery_allocations: it.delivery_allocations,
                                };
                            } else if (it.product?.id) {
                                // New product item: send product ID with item_kind
                                return {
                                    id: it.product.id,
                                    item_kind: "PRODUCT",
                                    quantity: it.quantity,
                                delivery_allocations: it.delivery_allocations,
                                };
                            } else if (it.material?.id) {
                                return {
                                    id: it.material.id,
                                    item_kind: "MATERIAL",
                                    quantity: it.quantity,
                                delivery_allocations: it.delivery_allocations,
                                };
                            } else if (it.id && it.item_kind) {
                                return {
                                    id: it.id,
                                    item_kind: it.item_kind,
                                    quantity: it.quantity,
                                delivery_allocations: it.delivery_allocations,
                                };
                            } else {
                                console.warn("PATCH: Item does not have valid product/material ID or delivery_trip_item_id:", it);
                                return null;
                            }
                        })
                        .filter(Boolean); // Only include valid items

                    await apiMethods.patch(`/product-flow/v2/delivery-trips/${tripId}/header/`, {
                        vehicle_plate: selectedCarPlateCache.get(formData.car_plate) || formData.car_plate,
                        trip_number: formData.trip_number ? Number(formData.trip_number) : null,
                        scheduled_at: formData.delivery_date == null ? null : dayjs(formData.delivery_date).utc().format("YYYY-MM-DDTHH:mm:ss"),
                        notes: formData.delivery_note || "No notes",
                        ...(!tripNumberLocked ? {items: updateItems, replace_items: true} : {}),
                    });
                }

                // 3c) Handle return quantities for items (shared helper)
                await handleReturns(tripId, savedItems, productIdToLineItemId, materialIdToLineItemId);
            }

            // 5) Update parent state instead of full reload
            mergeTripIntoState(tripId, savedItems, isNewTrip);
            await fetchProductOrder?.();

            // Show success message
            if (isNewTrip) {
                success("THÀNH CÔNG", "Lưu chuyến vận chuyển thành công");
            } else {
                success("THÀNH CÔNG", "Cập nhật chuyến vận chuyển thành công");
            }
            handleClose();
        } catch (err: any) {
            console.error("Error saving delivery trip:", err);
            console.error("Error details:", err?.data || err?.message || err);
            error("Lỗi lưu chuyến vận chuyển", err);
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        if (isSavingRef.current) return;
        setValidated(true);

        if (formData.delivery_date != null && !dayjs(formData.delivery_date).isValid()) {
            alert("Ngày giao hàng không hợp lệ.");
            return;
        }
        // Product selection is optional - allow empty delivery trips
        // if (selectedItems.length === 0) {
        //     alert("Vui lòng chọn ít nhất một sản phẩm.");
        //     return;
        // }
        if (selectedItems.length > 0 && selectedItems.some((item) => !Number.isInteger(Number(item.quantity)) || Number(item.quantity) <= 0)) {
            alert("Vui lòng nhập số lượng cho tất cả các sản phẩm đã chọn.");
            return;
        }

        // Prepare data
        const updated = await prepareDeliveryTrip(selectedItems);
        const updatedTrips: DeliveryTrip[] = [...(preparedDeliveryTrips || [])];
        const existingIndex = updatedTrips.findIndex(trip => trip.id === updated.id);

        if (existingIndex !== -1) {
            updatedTrips[existingIndex] = updated;
        } else {
            updatedTrips.push(updated);
        }

        // Store prepared data
        setPreparedDeliveryTrip(updated);
        setPreparedDeliveryTrips(updatedTrips);

        // Validate and show appropriate modal
        const messages = validateQuantities(productOrder, updatedTrips);
        setMessages(messages);

        // If no validation errors, save the trip directly to backend
        if (messages.length === 0) {
            await saveDeliveryTrip();
        }
    };

    const [messages, setMessages] = useState<string[]>([]);

    useEffect(() => {
        if (isEmpty(preparedDeliveryTrips)) return;
        if (messages.length > 0) {
            setCancelModalMessage(messages.join("\n"));
            setShowCancelModal(true);
        }
        // Removed automatic save - let user click save button manually
    }, [preparedDeliveryTrips, messages]);

    useEffect(() => {
        if (open) {
            setIsOpen(true);
        } else {
            initializedDraftRef.current = null;
            const timer = setTimeout(() => setIsOpen(false), 200);
            return () => clearTimeout(timer);
        }

        // Only initialize a fresh draft; polling/prop identity changes must not
        // overwrite the user's address or an existing trip's saved snapshot.
        if (initializedDraftRef.current === draftKey) return;
        initializedDraftRef.current = draftKey;
        previousDefaultAddressRef.current = defaultAddress;
        setValidated(false);
        setRemovedItems([]);
        setPreparedDeliveryTrips(productOrder.delivery_trips || []);

        const orderRows = [
                ...(productOrder.products || []).map(product => ({id: product.id, product, quantity: getSuggestedDeliveryQuantity(product), returned_quantity: 0, item_kind: "PRODUCT"})),
                ...(productOrder.materials || []).map(material => ({id: material.id, material, quantity: getSuggestedDeliveryQuantity(material), returned_quantity: 0, item_kind: "MATERIAL"})),
            ].map(item => ({...item, rowKey: rowKey(), delivery_address: defaultAddress})) as DeliveryRow[];
        if (isEditingTrip) {
            setFormData({
                trip_number: selectedDeliveryTrip.trip_number == null ? "" : String(selectedDeliveryTrip.trip_number),
                car_plate: selectedDeliveryTrip.car_plate || selectedDeliveryTrip.vehicle_plate || "",
                delivery_date: selectedDeliveryTrip.scheduled_at === null ? null : (selectedDeliveryTrip.scheduled_at ? new Date(selectedDeliveryTrip.scheduled_at) : (selectedDeliveryTrip.date ? new Date(selectedDeliveryTrip.date) : null)),
                delivery_note: selectedDeliveryTrip.note || selectedDeliveryTrip.notes || "",
            });
            // Use normalized_items first, fallback to delivery_products or items
            const items = (selectedDeliveryTrip as any).normalized_items || selectedDeliveryTrip.delivery_products || (selectedDeliveryTrip as any).items || [];
            setSelectedItems(expandDeliveryRows(items, selectedDeliveryTrip.delivery_address || defaultAddress));
            const existingKeys = new Set(items.map(entityKey));
            setRemovedItems(orderRows.filter(item => !existingKeys.has(entityKey(item))));
        } else if (open) {
            setFormData({
                trip_number: "",
                car_plate: "",
                delivery_date: new Date() as Date | number | null,
                delivery_note: "",
            });
            setSelectedItems(orderRows);
        }
    }, [open, draftKey, isEditingTrip, selectedDeliveryTrip, productOrder.delivery_trips, defaultAddress]);

    useEffect(() => {
        const previousDefault = previousDefaultAddressRef.current;
        previousDefaultAddressRef.current = defaultAddress;
        if (!open || isEditingTrip || !defaultAddress || defaultAddress === previousDefault) return;
        setSelectedItems(rows => rows.map(row => !row.addressTouched && (!row.delivery_address || row.delivery_address === previousDefault)
            ? { ...row, delivery_address: defaultAddress } : row));
    }, [open, isEditingTrip, defaultAddress]);

    return (
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
            sx={{
                "& .MuiPaper-root": {
                    width: "1000px",
                    maxWidth: "90%",
                    margin: "0 auto",
                    height: "1200px",
                },
            }}
        >
            <ModalWrapper isOpen={isOpen} style={{ width: "1000px", margin: "0 auto", height: "1200px" }}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>{selectedDeliveryTrip?.id ? "Chỉnh sửa chuyến vận chuyển" : "Thêm chuyến vận chuyển"}</TitleText>
                    </TitleWrapper>
                    <Form
                        title=""
                        fields={formFields}
                        validated={validated}
                        requireData={formData}
                        mergeFormData={handleFormDataChange}
                        uuidCache={new Map()} // No longer needed for car plates
                        style={{
                            contentWrapper: { padding: 0, border: "none", boxShadow: "none" },
                            formWrapper: { marginTop: 0, border: "none", boxShadow: "none" },
                            fieldWrapper: {
                                width: "calc(50% - 8px)",
                                display: "inline-block",
                                marginRight: "8px",
                                verticalAlign: "top",
                            },
                            input: { width: "100%" },
                            textBox: { width: "100%" },
                        }}
                    />
                    {/* Custom Table */}
                    <CustomTable
                        itemList={selectedItems.map(item => ({...item, line_item_id: item.rowKey}))}
                        RowElement={AddDeliveryTripModalRow}
                        TableColumns={AddDeliveryTripTableColumns}
                        extraRowProps={{
                            selectedItems,
                            deliveryAddresses: productOrder.customer?.delivery_addresses || [],
                            removeItem: removeSelectedItem,
                            splitItem,
                            canEdit: canEdit && !tripNumberLocked,
                            updateItem: (updatedItem: DeliveryRow) => {
                                setSelectedItems(rows => rows.map(item => item.rowKey === updatedItem.rowKey
                                    ? { ...updatedItem, addressTouched: item.addressTouched || updatedItem.delivery_address !== item.delivery_address }
                                    : item));
                            },
                            deliveryTripStatus: selectedDeliveryTrip?.status || null,
                            isMutating: isSaving,
                        }}
                        containerStyle={{ maxHeight: "300px" }}
                    />
                    {canEdit && removedItems.length > 0 && <div>
                        <TitleText>Đã bỏ khỏi chuyến này</TitleText>
                        {removedItems.map(item => <div key={item.rowKey} style={{display: "flex", alignItems: "center", gap: 12, marginTop: 8}}>
                            <span>{item.product?.name || item.material?.name} — {item.delivery_address}</span>
                            <StyledButton variant="outlined" onClick={() => {
                                setSelectedItems(rows => [...rows, item]);
                                setRemovedItems(rows => rows.filter(row => row.rowKey !== item.rowKey));
                            }}>Thêm lại</StyledButton>
                        </div>)}
                    </div>}
                </Expandable>
                {showCancelModal && (
                    <CancelModal
                        open={showCancelModal}
                        title="Thông báo số lượng sản phẩm"
                        contentText={cancelModalMessage}
                        handleConfirm={async () => {
                            setShowCancelModal(false);
                            await saveDeliveryTrip();
                        }}
                        handleClose={() => setShowCancelModal(false)}
                        isLoading={isSaving}
                    />
                )}


                <ButtonRow>
                    <StyledButton onClick={handleCloseWithAnimation} disabled={isSaving}>
                        Huỷ bỏ
                    </StyledButton>
                    <LoadingButton
                        variant="contained"
                        onClick={() => {
                            handleSave();
                        }}
                        isLoading={isSaving}
                        disabled={isSaving}
                        text="Lưu chuyến vận chuyển"
                    />
                </ButtonRow>
            </ModalWrapper>
        </Modal>
    );
});
